import React, { useState } from 'react';
import { Loader2, Bot } from 'lucide-react';
import { config } from '../config';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setIsSalespersonHost, clearConversation } from '../store/conversationSlice';
import { clearLandmineAlerts } from '../store/slices/landmineSlice';
import { clearCompetitorAlerts } from '../store/slices/competitorSlice';
import { store } from '../store';
import { CalendarBot } from '../services/calendarWebSocketService';
import { generateClientId } from '../utils/clientId';
import SessionSummaryModal from './SessionSummaryModal';
import { useSessionSummary } from '../hooks/useSessionSummary';

interface MeetBotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBotCreated: (sessionId: string, websocketUrl: string) => void | Promise<void>;
  isBotConnected?: boolean; // Track if bot is connected from parent
  onBotConnectionStateChange?: (isConnected: boolean) => void; // Callback to update parent state
  botType?: 'manual' | 'calendar' | null; // Type of connected bot
  botSessionId?: string | null; // Session ID of connected bot (for API calls)
  botDisplayId?: string | null; // Bot ID to display in UI
  setCalendarBots?: React.Dispatch<React.SetStateAction<CalendarBot[]>>; // For adding manual bots to calendar bots state
  defaultBotName?: string; // Default bot name from calendar settings
}

export const MeetBotModal: React.FC<MeetBotModalProps> = ({
  isOpen,
  onClose,
  onBotCreated,
  isBotConnected: parentBotConnected = false,
  onBotConnectionStateChange,
  botType = null,
  botSessionId = null,
  botDisplayId = null,
  setCalendarBots,
  defaultBotName,
}) => {

  const dispatch = useAppDispatch();

  // Get transcripts for checking if we need to clear previous session
  const transcripts = useAppSelector((state) => state.conversation.transcripts);

  // Session summary hook
  const {
    showSessionSummary,
    sessionSummaryData,
    computeAndShowSummary,
    closeSummary,
    clearSessionData,
  } = useSessionSummary();

  const [meetingUrl, setMeetingUrl] = useState('');
  const [eventTitle, setEventTitle] = useState('');  // Session/meeting name (mandatory)
  const [botName, setBotName] = useState(defaultBotName || 'Transcriber');
  const [isLoading, setIsLoading] = useState(false);

  // Update bot name when defaultBotName changes (after calendar settings load)
  React.useEffect(() => {
    if (defaultBotName && botName === 'Transcriber') {
      setBotName(defaultBotName);
    }
  }, [defaultBotName]);
  const [loadingMessage, setLoadingMessage] = useState('Creating Bot...');
  const [error, setError] = useState<string | null>(null);
  const [detectedPlatform, setDetectedPlatform] = useState<'meet' | 'zoom' | 'teams' | null>(null);
  const [isSalespersonHost, setLocalSalespersonHost] = useState(true);
  const [isBotConnected, setIsBotConnected] = useState(false);
  const [connectedBotId, setConnectedBotId] = useState<string | null>(null);

  // Sync modal state with parent's bot connection state
  React.useEffect(() => {
    if (isOpen) {
      // When modal opens or parent state changes, sync with parent's bot connection state
      setIsBotConnected(parentBotConnected);

      // If bot was terminated externally (parentBotConnected became false), clear internal state
      if (!parentBotConnected) {
        setConnectedBotId(null);
        setDetectedPlatform(null);
        console.log('🔄 Modal synced with parent: bot disconnected');
      }
    }
  }, [isOpen, parentBotConnected, botType, botSessionId, botDisplayId]);

  const detectPlatform = (url: string): 'meet' | 'zoom' | 'teams' | null => {
    const meetPattern = /^https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}$/;
    const zoomPatterns = [
      /^https?:\/\/[\w.-]*zoom\.us\/j\/(\d{9,11})(\?pwd=[\w.-]+)?$/,
      /^https?:\/\/[\w.-]*zoom\.us\/my\/([\w.-]+)(\?pwd=[\w.-]+)?$/,
      /^https?:\/\/[\w.-]*zoom\.us\/w\/(\d{9,11})(\?pwd=[\w.-]+)?$/,
      /^https?:\/\/[\w.-]*zoomgov\.com\/j\/(\d{9,11})(\?pwd=[\w.-]+)?$/
    ];
    const teamsPattern = /^https:\/\/teams\.microsoft\.com\/l\/meetup-join\//;

    if (meetPattern.test(url)) {
      return 'meet';
    }

    for (const pattern of zoomPatterns) {
      if (pattern.test(url)) {
        return 'zoom';
      }
    }

    if (teamsPattern.test(url)) {
      return 'teams';
    }

    return null;
  };

  const handleCreateBot = async () => {
    if (!meetingUrl.trim()) {
      setError('Please enter a valid meeting URL');
      return;
    }

    if (!eventTitle.trim()) {
      setError('Please enter a session name');
      return;
    }

    // Auto-detect platform from URL
    const platform = detectPlatform(meetingUrl);

    if (!platform) {
      setError('Please enter a valid Google Meet, Zoom, or Microsoft Teams URL');
      return;
    }

    // Store detected platform for display
    setDetectedPlatform(platform);

    // Confirm with user before creating bot
    const platformName = platform === 'meet' ? 'Google Meet' : platform === 'zoom' ? 'Zoom' : 'Microsoft Teams';
    const roleName = isSalespersonHost ? 'host' : 'participant';

    const confirmed = window.confirm(
      `Are you sure you want to join the ${platformName} meeting where you are the ${roleName}?`
    );

    if (!confirmed) {
      return; // User cancelled
    }

    // Clear previous session data if exists
    // Note: All session data is automatically saved by saver_service in real-time
    // No need to prompt user for manual save
    if (transcripts.length > 0) {
      const { sessionManager } = await import('../services/sessionManager');

      console.log('🧹 Clearing previous session data before creating new bot (auto-saved by saver_service)');

      // Clear all Redux state
      dispatch(clearConversation());
      dispatch(clearLandmineAlerts());
      dispatch(clearCompetitorAlerts());
      sessionManager.resetCallTiming();

      console.log('✅ Previous session data cleared');
    }

    setIsLoading(true);
    setLoadingMessage('Creating Bot...');
    setError(null);

    try {
      // Get Firebase auth token and user info
      const state = store.getState();
      const firebaseUid = state.auth.user?.uid;

      if (!firebaseUid) {
        throw new Error('User not authenticated');
      }

      // Get Firebase ID token for authentication
      const { auth } = await import('../config/firebase');
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const idToken = await currentUser.getIdToken();

      // Generate client ID for this session
      const clientId = generateClientId();
      console.log('📱 Generated clientId for bot creation:', clientId);

      // Use backend server bot endpoints (integrated bot service)
      const backendUrl = config.server.backendUrl || 'http://localhost:5000';

      const response = await fetch(`${backendUrl}/api/bots/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          meetingUrl,
          platform,
          eventTitle,
          firebaseUid,
          clientId,
          options: {
            botName,
            isSalespersonHost,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create bot');
      }

      // Store bot_id in localStorage and sessionManager so all services can use it
      const botId = data.botId || data.sessionId; // Some services return botId, others return sessionId
      const sessionId = data.sessionId; // Session ID is needed for termination
      const websocketUrl = data.websocketUrl;
      // Store isSalespersonHost in Redux store so transcript processing knows how to tag speakers
      dispatch(setIsSalespersonHost(isSalespersonHost));

      // Add manual bot to calendarBots state for lifecycle tracking
      // This allows calendar_sync bot_status_poll_loop to monitor termination
      if (setCalendarBots) {
        const manualBot: CalendarBot = {
          source: 'manual',
          botSessionId: `manual_${botId}`,
          botId: botId,
          eventTitle: eventTitle,
          meetingUrl: meetingUrl,
          startTime: new Date().toISOString(),
          status: 'joining',
          platform: platform,
          websocketUrl: websocketUrl,
          isSalespersonHost: isSalespersonHost,
        };

        // Note: Don't add to calendarBots manually - calendar_sync_service will pick it up
        // and send it via WebSocket after backend creates scheduledbots document
        console.log('✅ Manual bot will be added to calendarBots by calendar_sync_service:', manualBot);
      }

      // Note: scheduledbots document is now created during /api/bots/create call
      // No separate registration needed - backend creates the document immediately
      console.log('✅ Manual bot registered in MongoDB during creation (no separate registration needed)');

      // Update loading message for reconnection phase
      setLoadingMessage('Connecting to WebSocket...');

      // Call onBotCreated with both botId and websocketUrl
      try {
        const result = onBotCreated(botId, websocketUrl);
        if (result && typeof result === 'object' && 'then' in result) {
          await result;
        }

        // Update loading message to wait for bot to become active
        setLoadingMessage('Waiting for bot to be accepted into the meeting...');

        // Wait for the bot to actually become ACTIVE in the meeting (not just in lobby)
        // The bot-active event is dispatched by ConversationPage when calendar_sync_service
        // reports the bot status as 'active' (truly in the meeting, not just lobby)
        const botActivePromise = new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Bot took too long to be accepted into the meeting (timeout: 180s)'));
          }, 180000); // 180 second timeout (longer to account for lobby wait time)

          const handleBotActive = (event: any) => {
            // Only resolve if this is the bot we created
            if (event.detail?.botId === botId) {
              console.log('📌 Bot active event received for our bot:', event.detail);
              clearTimeout(timeout);
              window.removeEventListener('bot-active', handleBotActive);
              resolve();
            } else {
              console.log('📌 Bot active event for different bot, ignoring:', event.detail?.botId);
            }
          };

          window.addEventListener('bot-active', handleBotActive);
        });

        // Wait for bot to become truly active in the meeting
        await botActivePromise;

        console.log('✅ Bot has been accepted and is now active in the meeting!');

        // Mark bot as connected - store sessionId for termination, but display botId
        setIsBotConnected(true);
        setConnectedBotId(sessionId); // Use sessionId for API calls
        setMeetingUrl('');
        setError(null);

        // Notify parent component of bot connection state change
        if (onBotConnectionStateChange) {
          onBotConnectionStateChange(true);
        }

        // Auto-close modal after successful connection
        console.log('🔒 Auto-closing modal after successful bot connection');
        onClose();
      } catch (reconnectError: any) {
        console.error('❌ Error during service reconnection or bot join:', reconnectError);

        // Note: Event listeners are cleaned up in the promise handlers

        // Show appropriate error message
        if (reconnectError.message?.includes('timeout')) {
          setError('Bot created but took too long to join the meeting. Please check the meeting URL and try again.');
        } else {
          setError('Bot created but failed to connect properly. Please try again.');
        }
        setIsLoading(false);
        return;
      }

    } catch (err: any) {
      setError(err.message || 'Failed to create bot');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveBot = async () => {
    // For calendar bots: use parent props (botType, botSessionId)
    // For manual bots: use local state (connectedBotId)
    const isCalendarBot = botType === 'calendar';
    const sessionIdToRemove = isCalendarBot ? botSessionId : connectedBotId;


    if (!sessionIdToRemove) {
      console.error('❌ No session ID to remove! Cannot proceed.');
      alert('Error: No bot session ID found. Please try reconnecting the bot.');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to remove the bot from the meeting?');
    if (!confirmed) return;

    setIsLoading(true);
    setLoadingMessage('Removing bot...');

    try {
      // Check if this is a calendar bot
      if (botType === 'calendar') {
        console.log('📅 Removing calendar bot:', sessionIdToRemove);

        // Import calendar service
        const { calendarWebSocketService } = await import('../services/calendarWebSocketService');

        // Use calendar bot termination API
        const result = await calendarWebSocketService.terminateBot(sessionIdToRemove);

        if (!result.success) {
          throw new Error(result.error || 'Failed to remove calendar bot');
        }

        console.log('✅ Calendar bot removed successfully');
      } else {
        // Manual bot - use backend server bot endpoints
        console.log('🤖 Removing manual bot:', sessionIdToRemove);

        const backendUrl = config.server.backendUrl || 'http://localhost:5000';

        const response = await fetch(`${backendUrl}/api/bots/${sessionIdToRemove}/terminate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to remove bot');
        }

        console.log('✅ Manual bot removed successfully');

        // No need to update calendar service - backend now updates MongoDB directly
        // calendar_sync_service poll loop will detect the termination and notify saver_service
      }

      // Set call end time before disconnecting
      const { sessionManager } = await import('../services/sessionManager');
      sessionManager.setCallEndTime();

      // Disconnect from ALL WebSocket services
      console.log('🔌 Disconnecting from all WebSocket services...');

      const { recallWebSocketService } = await import('../services/recallWebSocketService');
      const { websocketService } = await import('../services/websocketService');
      const { landmineWebSocketService } = await import('../services/landmineWebSocketService');
      const { competitorWebSocketService } = await import('../services/competitorWebSocketService');

      recallWebSocketService.disconnect();
      websocketService.disconnect();
      landmineWebSocketService.disconnect();
      competitorWebSocketService.disconnect();

      console.log('✅ All WebSocket services disconnected');

      // Clear connection state
      setIsBotConnected(false);
      setConnectedBotId(null);
      setDetectedPlatform(null); // Clear detected platform
      setMeetingUrl(''); // Clear meeting URL

      // Reset isSalespersonHost to default in Redux store
      dispatch(setIsSalespersonHost(true));

      // CRITICAL: Notify parent component IMMEDIATELY that bot is disconnected
      // This updates the "Remove Bot" button to "Connect Bot"
      if (onBotConnectionStateChange) {
        console.log('🔴 Immediately updating parent: isBotConnected = false');
        onBotConnectionStateChange(false);
      }

      // Compute and show session summary using hook
      computeAndShowSummary(eventTitle || 'Session');

      // Note: Session is automatically saved by saver_service when bot disconnects
      // No manual save prompt needed - saver_service handles finalization automatically
      console.log('💾 Session automatically saved by saver_service');
      console.log('✅ Bot disconnected successfully');
    } catch (error: any) {
      console.error('❌ Error removing bot:', error);
      setError('Failed to remove bot. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSessionSummary = () => {
    closeSummary();
    onClose();
  };

  const handleClearSessionData = () => {
    clearSessionData();
    onClose();
    console.log('🧹 Session data cleared');
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-10">
      <div className="bg-white rounded-lg shadow-xl max-w-[80vw] max-h-[calc(100vh-80px)] overflow-y-auto">
        <div className="p-6 space-y-4 min-w-[600px]">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Bot className="w-6 h-6" />
              Add Meeting Bot to Call
            </h2>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${isBotConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                <span className="text-sm text-gray-600">
                  {isBotConnected ? 'Bot Active' : 'Not Connected'}
                </span>
              </div>
              <button
                onClick={onClose}
                disabled={isLoading}
                className={`text-2xl font-bold ${isLoading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
              >
                ×
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Show connected state if bot is connected */}
            {(() => {
              const displayBotType = botType === 'calendar' ? '📅 Calendar Bot' : botType === 'manual' ? '🤖 Manual Bot' : '🤖 Bot';
              const displayBotId = (botType === 'calendar' ? botDisplayId : connectedBotId) || 'Connecting...';


              return isBotConnected ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                        <div>
                          <h4 className="font-semibold text-green-900">Bot Connected</h4>
                          <p className="text-sm text-green-700">
                            Bot is actively monitoring the meeting
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h4 className="font-medium text-sm mb-2 text-gray-900">Connection Details:</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Bot Type: {displayBotType}</li>
                      <li>• Bot ID: {displayBotId}</li>
                      <li>• Platform: {detectedPlatform === 'meet' ? 'Google Meet' : detectedPlatform === 'zoom' ? 'Zoom' : detectedPlatform === 'teams' ? 'Microsoft Teams' : 'Auto-detected'}</li>
                      <li>• Salesperson Role: {isSalespersonHost ? 'Host' : 'Participant'}</li>
                      <li>• Transcripts are being received in real-time</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                  <label htmlFor="meeting-url" className="block text-sm font-medium text-gray-700">
                    Meeting URL
                  </label>
                  <input
                    id="meeting-url"
                    type="url"
                    placeholder="Enter Google Meet, Zoom, or Teams URL"
                    value={meetingUrl}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setMeetingUrl(e.target.value);
                      // Auto-detect platform when URL changes
                      if (e.target.value) {
                        const detected = detectPlatform(e.target.value);
                        if (detected) {
                          setDetectedPlatform(detected);
                          console.log(`Detected platform: ${detected}`);
                        }
                      }
                    }}
                    disabled={isLoading}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  {detectedPlatform && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>
                        Detected: {detectedPlatform === 'meet' ? 'Google Meet' : detectedPlatform === 'zoom' ? 'Zoom' : 'Microsoft Teams'}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Enter any meeting URL - we'll automatically detect the platform
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="event-title" className="block text-sm font-medium text-gray-700">
                    Session Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="event-title"
                    type="text"
                    placeholder="Enter a name for this session (e.g., Client Demo, Sales Call)"
                    value={eventTitle}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventTitle(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500">
                    Required: This name will be used to identify this session in your records
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="bot-name" className="block text-sm font-medium text-gray-700">
                    Bot Display Name
                  </label>
                  <input
                    id="bot-name"
                    type="text"
                    placeholder="Sales Guru Assistant"
                    value={botName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBotName(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500">
                    This name will be shown to participants in the meeting
                  </p>
                  <p className="text-xs text-gray-400">
                    Suggested names: Transcriber, Note Taker, Meeting Assistant, Scribe
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="host-selection" className="block text-sm font-medium text-gray-700">
                    Salesperson Role
                  </label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        value="participant"
                        checked={!isSalespersonHost}
                        onChange={() => setLocalSalespersonHost(false)}
                        disabled={isLoading}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Participant</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        value="host"
                        checked={isSalespersonHost}
                        onChange={() => setLocalSalespersonHost(true)}
                        disabled={isLoading}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Host</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Select whether the salesperson is the host or a participant in the meeting
                  </p>
                </div>
                </>
              );
            })()}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h4 className="font-medium text-sm mb-2 text-gray-900">How it works:</h4>
              <ol className="text-sm text-gray-700 space-y-1">
                <li>1. The bot will join your meeting automatically</li>
                <li>2. It will capture and transcribe audio in real-time</li>
                <li>3. Live analysis will appear in your dashboard</li>
                <li>4. The bot stays muted and camera-off by default</li>
                {detectedPlatform === 'zoom' && (
                  <li className="text-xs text-gray-600 mt-1">Note: Host may need to admit the bot from waiting room</li>
                )}
              </ol>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            {isBotConnected ? (
              <>
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Close
                </button>
                <button
                  onClick={handleRemoveBot}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {loadingMessage}
                    </>
                  ) : (
                    'Remove Bot'
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBot}
                  disabled={isLoading || !meetingUrl.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {loadingMessage}
                    </>
                  ) : (
                    'Add Bot to Meeting'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Session Summary Modal */}
      {sessionSummaryData && (
        <SessionSummaryModal
          isOpen={showSessionSummary}
          onClose={handleCloseSessionSummary}
          onClearSession={handleClearSessionData}
          summary={sessionSummaryData}
        />
      )}
    </div>
  );
};